import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Loader2 } from 'lucide-react';
import { renamePipeId } from '../../../services/pipeIdEditApi';
import { logPipeIdRenamed } from '../utils/pipefitterAudit';
import { Notification as customNotification } from '../../../components/ui/custom-notification';

export interface UpdatePipeIdAuditContext {
  projectId?: number | null;
  projectName?: string | null;
  projectNumber?: string | null;
}

export interface UpdatePipeIdDialogPipe {
  id: number | string;
  pipeID: string;
  endA?: boolean;
  endB?: boolean;
  pipeEndIDA?: string | null;
  pipeEndIDB?: string | null;
}

interface UpdatePipeIdDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pipe: UpdatePipeIdDialogPipe | null;
  auditContext?: UpdatePipeIdAuditContext;
  onRenamed?: (result: { id: number; oldPipeID: string; newPipeID: string }) => void;
}

function stripEndSuffix(value: string): string {
  return String(value || '')
    .trim()
    .replace(/EndA$/i, '')
    .replace(/EndB$/i, '');
}

/**
 * Status often stores markers ("EndA"/"EndB"); pipeinfo uses `{pipeID}EndA`.
 * Always return the full pipeinfo key for API / preview.
 */
function resolveCurrentEndId(
  pipe: UpdatePipeIdDialogPipe | null,
  end: 'EndA' | 'EndB'
): string {
  if (!pipe) return '';
  const base = stripEndSuffix(pipe.pipeID || '');
  const raw = String(
    end === 'EndA' ? pipe.pipeEndIDA || '' : pipe.pipeEndIDB || ''
  ).trim();
  if (!raw || /^EndA$/i.test(raw) || /^EndB$/i.test(raw)) {
    return base ? `${base}${end}` : '';
  }
  return raw;
}

export const UpdatePipeIdDialog: React.FC<UpdatePipeIdDialogProps> = ({
  open,
  onOpenChange,
  pipe,
  auditContext,
  onRenamed,
}) => {
  const [basePipeId, setBasePipeId] = useState('');
  const [savingRename, setSavingRename] = useState(false);

  // Include whichever ends exist — no UI toggle.
  const updateEndA = Boolean(pipe?.endA || pipe?.pipeEndIDA);
  const updateEndB = Boolean(pipe?.endB || pipe?.pipeEndIDB);
  const currentEndA = useMemo(() => resolveCurrentEndId(pipe, 'EndA'), [pipe]);
  const currentEndB = useMemo(() => resolveCurrentEndId(pipe, 'EndB'), [pipe]);

  useEffect(() => {
    if (!open || !pipe) return;
    setBasePipeId(stripEndSuffix(pipe.pipeID || ''));
  }, [open, pipe]);

  const statusId = pipe?.id != null ? Number(pipe.id) : NaN;
  const projectNumber = String(auditContext?.projectNumber || '').trim();

  const handleRename = async () => {
    if (!pipe || !Number.isFinite(statusId) || statusId <= 0 || !projectNumber) return;
    const trimmed = basePipeId.trim();
    if (!trimmed) {
      customNotification.error('Pipe ID required', 'Enter the pipe ID (without EndA/EndB).');
      return;
    }
    if (!updateEndA && !updateEndB) {
      customNotification.error('No ends', 'This pipe has no End A or End B to update.');
      return;
    }

    const nextEndA = `${trimmed}EndA`;
    const nextEndB = `${trimmed}EndB`;
    const pipeIdChanged = trimmed !== stripEndSuffix(pipe.pipeID || '');
    const endAChanged = updateEndA && nextEndA !== currentEndA;
    const endBChanged = updateEndB && nextEndB !== currentEndB;

    if (!pipeIdChanged && !endAChanged && !endBChanged) {
      customNotification.error('No change', 'Change the pipe ID to update.');
      return;
    }

    setSavingRename(true);
    try {
      const result = await renamePipeId({
        id: statusId,
        projectNumber,
        // Keep status.pipeID in sync with the edited base value.
        newPipeID: trimmed,
        updateEndA,
        updateEndB,
        ...(updateEndA
          ? { newPipeEndIDA: nextEndA, oldPipeEndIDA: currentEndA }
          : {}),
        ...(updateEndB
          ? { newPipeEndIDB: nextEndB, oldPipeEndIDB: currentEndB }
          : {}),
      });

      const endChanges: Array<{
        end: 'EndA' | 'EndB';
        beforePipeEndId: string;
        afterPipeEndId: string;
        beforeFileUrl?: string | null;
        afterFileUrl?: string | null;
      }> = [];
      if (endAChanged) {
        endChanges.push({
          end: 'EndA',
          beforePipeEndId: currentEndA,
          afterPipeEndId: nextEndA,
          beforeFileUrl:
            result.s3Updates?.find((item) => item.end === 'EndA')?.oldFileUrl ?? null,
          afterFileUrl:
            result.s3Updates?.find((item) => item.end === 'EndA')?.newFileUrl ?? null,
        });
      }
      if (endBChanged) {
        endChanges.push({
          end: 'EndB',
          beforePipeEndId: currentEndB,
          afterPipeEndId: nextEndB,
          beforeFileUrl:
            result.s3Updates?.find((item) => item.end === 'EndB')?.oldFileUrl ?? null,
          afterFileUrl:
            result.s3Updates?.find((item) => item.end === 'EndB')?.newFileUrl ?? null,
        });
      }

      if (pipeIdChanged || endChanges.length > 0) {
        logPipeIdRenamed({
          projectId: auditContext?.projectId,
          projectName: auditContext?.projectName,
          projectNumber,
          statusId,
          beforePipeId: pipe.pipeID,
          afterPipeId: result.newPipeID || trimmed,
          endChanges,
          s3Updates: result.s3Updates,
        });
      }

      const s3Failed = (result.s3Errors?.length ?? 0) > 0;
      if (s3Failed) {
        customNotification.warning(
          'Pipe ID updated',
          'Database updated, but one or more S3 files could not be renamed. Check audit log for details.'
        );
      } else {
        customNotification.success('Pipe ID updated');
      }
      onRenamed?.({
        id: statusId,
        oldPipeID: pipe.pipeID,
        newPipeID: result.newPipeID || trimmed,
      });
      onOpenChange(false);
    } catch (error: any) {
      customNotification.error(
        'Update failed',
        error?.response?.data?.message || error?.message || 'Please try again.'
      );
    } finally {
      setSavingRename(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !savingRename && onOpenChange(next)}>
      <DialogContent className="bg-white max-w-3xl w-full">
        <DialogHeader className="pl-0 pr-14 pb-4">
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-[18px] font-semibold text-blueprint-blue leading-tight">
              Edit Pipe ID
            </DialogTitle>
            <Button
              onClick={handleRename}
              disabled={!basePipeId.trim() || savingRename || !pipe || (!updateEndA && !updateEndB)}
              className="bg-hero-red hover:bg-hero-red-60 text-white"
            >
              {savingRename ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                'Update Pipe ID'
              )}
            </Button>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-[13px] text-text-grey">
            Current pipe ID{' '}
            <strong className="text-blueprint-blue">{pipe?.pipeID || '—'}</strong>.
            Available ends are updated automatically with the new pipe ID.
          </p>

          <div className="space-y-2">
            <Label htmlFor="base-pipe-id" className="text-[12px] font-medium text-text-grey">
              Pipe ID
            </Label>
            <Input
              id="base-pipe-id"
              value={basePipeId}
              onChange={(e) => setBasePipeId(e.target.value)}
              placeholder="e.g. SSN2-20-104"
              className="bg-white border-[#E2E5EA] text-text-grey placeholder:text-heritage-grey"
              disabled={savingRename}
              autoFocus
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UpdatePipeIdDialog;
